import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  dataAuthorizationAPI,
  DataAuthorizationRecord,
} from '../services/api';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  Download,
  Shield,
  User,
  Calendar,
} from 'lucide-react';

const DataAuthorizationList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DataAuthorizationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await dataAuthorizationAPI.list();
        setRows(data);
      } catch (err: any) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 404) {
          setError(
            'No se encontró el servicio en el servidor (404). Cierre y vuelva a iniciar el backend desde la carpeta del proyecto para cargar la última versión, y compruebe que escucha en el puerto 8004.'
          );
        } else {
          setError(
            (typeof detail === 'string' ? detail : null) ||
              err.message ||
              'Error al cargar las autorizaciones.'
          );
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-CO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const handleDownload = async (row: DataAuthorizationRecord) => {
    if (!row.id) return;
    setDownloadingId(row.id);
    try {
      const blob = await dataAuthorizationAPI.downloadPDF(row.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const nombre = (row.patient_data?.nombre as string) || 'paciente';
      const safe = nombre.replace(/[^a-z0-9]/gi, '_').substring(0, 28);
      link.download = `Autorizacion_datos_${safe}_${row.patient_document}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(
        'Error al descargar el PDF: ' +
          (err.response?.data?.detail || err.message || '')
      );
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <Loader className="mb-4 h-12 w-12 animate-spin text-blue-700" />
        <p className="font-medium text-slate-600">Cargando autorizaciones…</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-6xl mx-auto">
        <div className="page-hero mb-2">
          <button
            type="button"
            onClick={() => navigate('/templates')}
            className="btn-secondary mb-4 py-2 text-sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a plantillas
          </button>
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800 text-white shadow-md">
              <Shield className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
                Protección de datos
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Autorización de datos personales
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600 sm:text-base">
                Registros firmados en el primer consentimiento informado del paciente en esta
                aplicación (Ley 1581 de 2012). Descargue el PDF con membrete institucional.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="card mb-6 border-red-200 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <p className="font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="card py-12 text-center">
            <Shield className="mx-auto mb-4 h-14 w-14 text-slate-300" />
            <h3 className="mb-2 text-lg font-semibold text-slate-800">
              No hay autorizaciones registradas
            </h3>
            <p className="text-slate-600">
              Aparecerán aquí cuando un paciente sin historial previo firme la autorización al
              llenar su primer consentimiento informado.
            </p>
          </div>
        ) : (
          <div className="card overflow-x-auto p-0 sm:p-2">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-700">Paciente</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Documento</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Registrado</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">Usuario</th>
                  <th className="px-4 py-3 font-semibold text-slate-700">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const nombre = (row.patient_data?.nombre as string) || '—';
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 font-medium text-slate-900">
                          <User className="h-4 w-4 text-slate-400" aria-hidden />
                          {nombre}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{row.patient_document}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-slate-400" aria-hidden />
                          {formatDate(row.filled_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{row.filled_by}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleDownload(row)}
                          disabled={downloadingId === row.id}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-blue-800 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          {downloadingId === row.id ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          Descargar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataAuthorizationList;
